// eslint-disable-next-line import/no-cycle
import { RequestError } from 'Api';
import { pickObjectPropertiesByObject } from 'Utils';
import { BaseModel } from '../base';
import { IFormModelData } from './types';
import { BaseFormModelDataHandler } from './BaseFormModelDataHandler';
import { BaseFormModelErrorHandler } from './BaseFormModelErrorHandler';
import { BaseFormModelSender } from './BaseFormModelSender';

export abstract class BaseFormModel<ISendResult = unknown> {
  model?: BaseModel;
  formDataHandler: BaseFormModelDataHandler;
  formErrorHandler: BaseFormModelErrorHandler;
  formSender: BaseFormModelSender;

  // If true, will send all data not only changed
  allDataRequired = false;

  constructor(model?: BaseModel) {
    this.model = model;
    this.formDataHandler = new BaseFormModelDataHandler(this.getFields());
    this.formErrorHandler = new BaseFormModelErrorHandler();
    this.formSender = new BaseFormModelSender();
  }

  abstract get sendUrl(): string;

  get sendMethod() {
    return 'POST';
  }

  get data() {
    return this.formDataHandler.data;
  }

  get fieldsData() {
    return this.formDataHandler.getDataByFields(this.getFields());
  }

  get changedData() {
    return this.formDataHandler.getChangedData((this.model || {}) as IFormModelData, this.getFields());
  }

  get isChanged(): boolean {
    return Boolean(Object.keys(this.changedData).length);
  }

  /**
   * Get fields with default data for form.
   */
  abstract getFields(): IFormModelData;

  /**
   * Reset form fields to default data.
   */
  resetFields() {
    this.formDataHandler.setData(this.getFields());
  }

  setData(data: IFormModelData) {
    this.formDataHandler.setData(data);
  }

  setDefinedFieldsData(data: IFormModelData) {
    const filteredData = pickObjectPropertiesByObject(data, this.getFields());
    this.formDataHandler.setData(filteredData as IFormModelData);
  }

  getSendData(): unknown {
    return this.allDataRequired ? this.fieldsData : this.changedData;
  }

  beforeSend() {
    // Do something before send
  }

  onSendSuccess(result: ISendResult) {
    return result;
  }

  onSendError(error: unknown) {
    return error;
  }

  async send() {
    await this.beforeSend();
    const data = this.getSendData();
    try {
      const result = await this.formSender.send<ISendResult>(this.sendUrl, this.sendMethod, data);
      return await this.onSendSuccess(result);
    } catch (error) {
      this.onSendError(error);
      throw this.formErrorHandler.getNormalizedError(error as RequestError);
    }
  }
}
